import type { AdminDecisionsPageResponse, AdminDecisionsRangeQuery, DecisionAudit } from '@kkp-fraud/shared';

/**
 * "Every decision in this period" — the Report screen's read. One call pages through
 * `GET /admin/decisions?from=&to=&limit=&cursor=` until the API stops handing back a
 * `nextCursor`, or until the ceiling below, whichever comes first.
 *
 * Its own module (not `api.ts`) so the demo layer can page its fixtures through the SAME
 * loop without importing the client it wraps.
 */

/** Rows per request — the decision API's per-page maximum. */
export const RANGE_PAGE_SIZE = 5000;
/** Most rows one report holds in the browser. Past this the result says `capped`. */
export const RANGE_CEILING = 50_000;
/**
 * Most requests one range may take. A page may legitimately be short or even empty while a
 * cursor is still present (Cosmos cross-partition), so the loop follows the cursor, not the
 * row count — this is what stops a server that keeps answering with a cursor from spinning
 * the page forever.
 */
export const RANGE_MAX_PAGES = 100;

/** `[from, to)` as ISO 8601 instants. */
export interface DecisionRange {
  from: string;
  to: string;
}

export interface DecisionsRangeResult {
  /** Newest first, at most `RANGE_CEILING`. */
  items: DecisionAudit[];
  /** True when the period holds more rows than the page may load — the report is partial. */
  capped: boolean;
}

export interface RangeProgress {
  loaded: number;
  pages: number;
}

/** Called after every page. Return `false` to stop (the caller no longer wants this range). */
export type OnRangePage = (p: RangeProgress) => void | boolean;

export type FetchDecisionsPage = (q: Required<Pick<AdminDecisionsRangeQuery, 'from' | 'to' | 'limit'>> & { cursor?: string }) => Promise<AdminDecisionsPageResponse>;

export async function collectRange(fetchPage: FetchDecisionsPage, range: DecisionRange, onPage?: OnRangePage): Promise<DecisionsRangeResult> {
  const items: DecisionAudit[] = [];
  let cursor: string | undefined;
  for (let pages = 1; pages <= RANGE_MAX_PAGES; pages++) {
    const limit = Math.min(RANGE_PAGE_SIZE, RANGE_CEILING - items.length);
    const page = await fetchPage({ from: range.from, to: range.to, limit, ...(cursor ? { cursor } : {}) });
    items.push(...(Array.isArray(page?.items) ? page.items : []));
    cursor = typeof page?.nextCursor === 'string' && page.nextCursor !== '' ? page.nextCursor : undefined;
    if (onPage?.({ loaded: Math.min(items.length, RANGE_CEILING), pages }) === false) break;
    if (!cursor) return { items, capped: false };
    if (items.length >= RANGE_CEILING) return { items: items.slice(0, RANGE_CEILING), capped: true };
  }
  // Stopped by the caller, or out of pages with a cursor still in hand: either way not the whole period.
  return { items: items.slice(0, RANGE_CEILING), capped: cursor !== undefined };
}

/**
 * One page over rows already in memory, newest first — the demo layer's stand-in for the
 * API. Offset cursors are fine here: the rows are a fixed snapshot for the whole loop.
 */
export function pageOfRows(rows: readonly DecisionAudit[], q: Parameters<FetchDecisionsPage>[0]): AdminDecisionsPageResponse {
  const from = Date.parse(q.from);
  const to = Date.parse(q.to);
  const inRange = rows.filter((r) => {
    const at = Date.parse(r.eventTimeUtc);
    return at >= from && at < to;
  });
  const offset = q.cursor ? Math.max(0, Number.parseInt(q.cursor, 10) || 0) : 0;
  const end = offset + Math.max(1, q.limit);
  return { items: inRange.slice(offset, end), ...(end < inRange.length ? { nextCursor: String(end) } : {}) };
}
